import React, { useState } from "react";
import "../../../styles/TablesSBoardType2.css";
import createTableType2 from "./utils/createTableType2";
import { BoardProps, InputRoundType2 } from "../../types/types";
import normalizeWord from "../type1/utils/normalizeWord";
import shuffleArray from "../shuffleArray";

import { useGame } from "../../../GameContext";
import { useTimerActions } from "../../../TimerContext";

import RenderBoardLogo from "../RenderBoardLogo";
import RenderTableType2 from "./RenderTableType2";
import RenderGameCard from "../RenderGameCard";

import MESSAGES from "../message";

const BoardType2: React.FC<BoardProps> = ({ rounds, random, difficulty,type }) => {
  const { isGameStarted, setGameStarted, setMessage, setGuessesWords } =
    useGame();
  const { startTimer, stopTimer, resetTimer } = useTimerActions();

  const roundsData = rounds as InputRoundType2[];

  // функция для создания случайного порядка раундов
  const createRoundOrder = () => {
    const indexes = roundsData.map((_, index) => index);

    return random ? shuffleArray(indexes) : indexes;
  };

  // состояние для хранения порядка раундов
  const [roundOrder, setRoundOrder] = useState(() => createRoundOrder());

  // индекс текущего раунда
  const [roundIndex, setRoundIndex] = useState(0);

  // текущий раунд
  const currentRound = roundsData[roundOrder[roundIndex]];

  const isLastRound = roundIndex === roundsData.length - 1;

  // таблицы для всех раундов
  const [tables, setTables] = useState(() =>
    roundsData.map((round) =>
      createTableType2(round.text, round.size, difficulty),
    ),
  );

  // текущая таблица
  const table = tables[roundOrder[roundIndex]];

  // массив ответов для текущего раунда
  const answers = currentRound.text.split(/\s+/);

  //ответ, который нужно угадать в текущем раунде
  const [answerIndex, setAnswerIndex] = useState(0);
  const currentAnswer = answers[answerIndex];
  const isLastAnswer = answerIndex === answers.length - 1;

  // состояние, которое определяет, закончена ли игра
  const [isGameFinished, setIsGameFinished] = useState(false);

  // состояние, которое хранит выбранные пользователем клетки
  const [selectedCells, setSelectedCells] = useState<Set<string>>(new Set());

  const [answerValue, setAnswerValue] = useState("");

  const [blockUserInput, setBlockUserInput] = useState(true);

  const [answerStatus, setAnswerStatus] = useState<
    "idle" | "correct" | "wrong"
  >("idle");

  // функция для сброса значения ответа и статуса
  const resetAnswer = () => {
    setAnswerValue("");
    setAnswerStatus("idle");
  };

  // функция для сброса раунда
  const resetRound = () => {
    resetAnswer();
    setSelectedCells(new Set());
    setAnswerIndex(0);
  };
  // функция для сброса игры
  const resetGame = () => {
    resetTimer();
    setTables(
      roundsData.map((round) =>
        createTableType2(round.text, round.size, difficulty),
      ),
    );
    setRoundOrder(createRoundOrder());
    setRoundIndex(0);
    setGuessesWords([]);
    setIsGameFinished(false);
  };

  // функция для начала раунда
  const startRound = () => {
    resetRound();
    setBlockUserInput(false);
    setMessage(MESSAGES.START_TYPE2);
    startTimer();
  };

  // функция для завершения раунда
  const finishRound = () => {
    stopTimer();
    setBlockUserInput(true);
  };

  // функция для перезапуска игры
  const restartGame = () => {
    resetGame();
    startRound();
  };

  // функция для начала игры
  const handleStart = () => {
    setGameStarted(true);
    startRound();
  };

  // функция для перехода к следующему раунду
  const startNextRound = () => {
    setRoundIndex((prev) => prev + 1);
    startRound();
  };

  // функция для перехода к следующему раунду или завершения игры
  const nextGuessed = () => {
    // если игра закончена, то перезапускаем игру
    if (isGameFinished) {
      restartGame();
      return;
    }
    // если текущий раунд не последний, то переходим к следующему раунду
    startNextRound();
  };

  // функция для обработки неправильного ответа
  const handleWrongAnswer = (message: string) => {
    setAnswerStatus("wrong");
    setMessage(message);
  };

// функция для обработки правильного ответа
  const handleCorrectAnswer = (answer: string) => {
    setAnswerStatus("correct");
    setGuessesWords((prev) => [...prev, answer]);
  };

  // функция для перехода к следующему ответу
  const nextAnswer = () => {
    setAnswerIndex((prev) => prev + 1);

    resetAnswer();

    setMessage(MESSAGES.ENTER_TYPE2);
  };

  // функция для завершения текущего раунда
  const finishCurrentRound = () => {
    finishRound();

    if (isLastRound) {
      setIsGameFinished(true);
      setMessage(MESSAGES.GAME_FINISHED);
      return;
    }

    setMessage(MESSAGES.ROUND_FINISHED);
  };

  // функция для проверки ответа
  const checkAnswer = () => {
    if (blockUserInput || isGameFinished || !isGameStarted) {
      return;
    }

    const answer = normalizeWord(answerValue);

    if (!answer) {
      handleWrongAnswer(MESSAGES.ENTER_ANSWER);
      return;
    }

    if (answer !== normalizeWord(currentAnswer)) {
      handleWrongAnswer(MESSAGES.ANSWER_WRONG);
      return;
    }

    handleCorrectAnswer(answer);

    if (isLastAnswer) {
      finishCurrentRound();
      return;
    }

    nextAnswer();
  };

  // функция для переключения состояния выбранной клетки
  const toggleCell = (row: number, col: number) => {
    if (blockUserInput) return;

    const key = `${row}-${col}`;

    setSelectedCells((prev) => {
      const next = new Set(prev);

      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }

      return next;
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAnswerValue(e.target.value);

    if (answerStatus === "wrong") {
      setAnswerStatus("idle");
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== "Enter" || e.repeat) return;

    e.preventDefault();

    checkAnswer();

    e.currentTarget.blur();
  };

  return (
    <div className="tableS-Board">
      <div className="tableS-board-content">
        <RenderBoardLogo
          isGameStarted={isGameStarted}
          isGameFinished={isGameFinished}
          onStart={handleStart}
          onNext={nextGuessed}
          type = {type}
        />

        <div className="tableS-board-game">
          <RenderGameCard isGameFinished={isGameFinished}>
            <RenderTableType2
              table={table}
              selectedCells={selectedCells}
              onCellClick={toggleCell}
              isGameStarted={isGameStarted}
            />
          </RenderGameCard>

          <div className="tableS-button-input-board">
            <div className="tableS-subtitle">Введи ответ:</div>

            <input
              className={"tableS-input tableS-" + answerStatus}
              disabled={blockUserInput}
              value={answerValue}
              onFocus={() => setAnswerStatus("idle")}
              onChange={handleInputChange}
              onKeyDown={handleInputKeyDown}
            />

            {!blockUserInput && (
              <button
                className="tableS-buttonCheck"
                title="Проверить ответ"
                onClick={checkAnswer}
              >
                Проверить
              </button>
            )}
             {blockUserInput && isGameStarted && !isGameFinished && (
              <button className="tableS-buttonCheck" onClick={nextGuessed}>
                Далее
                <img
                  alt=""
                  className="tableS-logoRun"
                  src="/img/button/run-fix.svg"
                />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BoardType2;
